import React from "react";
import { type NextPage } from "next";
import { SimpleGrid } from "@chakra-ui/react";
import { Page } from "components/page";
import { FillImage } from "components/images";
import { Section, sectionSpacingY } from "components/section";
import { StackedLogos } from "components/logos";
import { CenteredQuote } from "components/quotes";
import { RealTimeHero } from "components/platform/real-time-personalization";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { Button } from "ui/button";
import { Diptych, DiptychColumn } from "ui/diptych";
import { GlobeIcon, LeversIcon, LightningIcon } from "ui/icons";
import { TextColumn, TextTitle } from "ui/text";
import { AuraPlate } from "ui/plates";
import firstDiptychImage from "../../../public/assets/platform/real-time-personalization/diptych-1.png";
import secondDiptychImage from "../../../public/assets/platform/real-time-personalization/diptych-2.png";
import thirdDiptychImage from "../../../public/assets/platform/real-time-personalization/diptych-3.png";

const PersonalizationAPI: NextPage<LandingPageProps> = ({
  page,
  announcementBanner,
  defaultLogos,
  defaultMobileLogos,
  customers,
}) => {
  const ramp = getSafeCustomer(customers, "ramp");

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.green.150"
      footerOptions={{ variant: "green" }}
    >
      <RealTimeHero eyebrow={page.eyebrow} title={page.title} description={page.description} />

      <StackedLogos
        my="0"
        pt={{ base: "big", md: "biggest" }}
        pb={sectionSpacingY}
        customers={defaultLogos}
        mobileCustomers={defaultMobileLogos}
      />

      <AuraPlate condensed variant="blue">
        <CenteredQuote
          maxW="3xl"
          author={{
            name: "Austin Hay",
            job: "Head of Marketing Technology",
          }}
          company={{
            name: "Ramp",
            logo: ramp?.logoUrl,
          }}
          quoteText="Hightouch’s real-time offering drastically simplifies personalization – giving teams the ability to experiment quickly."
          cta={{ href: "/customers/ramp", text: "Read customer story" }}
        />
      </AuraPlate>

      <Section>
        <TextTitle align="center" size="lg" title="Real-time personalization enriched by your Customer 360 data" />

        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          width="100%"
          gap={{ base: "biggest", md: "big" }}
          mt={{ base: "biggest", md: "huge" }}
          mb={sectionSpacingY}
        >
          <TextColumn
            iconChip={{ variant: "green", icon: LightningIcon }}
            title="Encourage customer loyalty"
            description="Show your customers that you value them with custom coupons tied to loyalty status or personalized recommendations tied to recent cart adds."
          />

          <TextColumn
            iconChip={{ variant: "blue", icon: LeversIcon }}
            title="Translate session time into conversions"
            description="People spend a lot of time on their phones. Drive personalized micro-transactions based on session time and known user preferences."
          />

          <TextColumn
            iconChip={{ variant: "violet", icon: GlobeIcon }}
            title="Create a unified brand experience"
            description="Send onsite personalization, SMS messages, and emails seconds after someone engages with your digital presence."
          />
        </SimpleGrid>

        <DiptychColumn>
          <Diptych
            size="lg"
            direction="right"
            media={
              <FillImage
                src={thirdDiptychImage}
                alt="Illustration of an e-commerce website interface featuring a checkout page with three items in the cart and an applied coupon discounting the total price."
              />
            }
            title="Update content and offers in real-time with same-session personalization"
            description="Keep customers engaged by merging current session data with historical user data to deliver responsive experiences instantly."
            actions={
              <Button hasRightChevron href="/blog/same-session-personalization">
                Read the blog
              </Button>
            }
          />
          <Diptych
            size="lg"
            direction="left"
            media={
              <FillImage
                src={firstDiptychImage}
                alt="Illustration of an SMS messaging platform interface featuring the latest text message titled 'Join us next weekend'. The expanded message features an ad for an upcoming retail pop-up event nearby and some recommended products based on user behavior."
              />
            }
            title="Trigger personalized marketing across channels in milliseconds"
            description="Reach your customers in real-time through channels like email or SMS with messages based on recent actions."
            actions={
              <Button hasRightChevron href="/blog/introducing-event-streaming">
                Read the blog
              </Button>
            }
          />

          <Diptych
            size="lg"
            direction="right"
            media={
              <FillImage
                src={secondDiptychImage}
                alt="Illustration of an e-commerce website interface featuring two sections labeled 'Recommended for you' with responsive apparel and electronics product recommendations and sales offers based on user behavior."
              />
            }
            title="Tailor website and in-app experiences to user preferences"
            description="Drive conversions by personalizing digital experiences, such as search results and product recommendations, based on what matters to each user."
            actions={
              <Button hasRightChevron href="/docs/destinations/personalization-api">
                Learn more
              </Button>
            }
          />
        </DiptychColumn>
      </Section>
    </Page>
  );
};

export default PersonalizationAPI;

export const getStaticProps = getLandingPageProps("/platform/real-time-personalization", {
  loadDefaultLogos: true,
  loadDefaultMobileLogos: true,
  loadCustomers: ["ramp"],
});
